import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { createEditor, Descendant, Range, Point, Transforms, Editor, BaseEditor, Element as SlateElement, Node } from 'slate';
import { Slate, Editable, withReact, ReactEditor, RenderElementProps, useSlateStatic } from 'slate-react';
import { ImageSvg } from '@/components/svgs';
import { usePlmImageUpload, serializeToHtml } from './board'
import { Button, App } from "antd"
import { patchWorkPackageAPI, postWorkPackageMsgAPI } from "@/services/ppm"
import { useRequest } from "ahooks"

const MENTION_CHAR = '@';
const initialValue: Descendant[] = [{
    type: 'paragraph',
    children: [{ text: '' }], // 空内容才能显示placeholder
}]
type CustomElement = ParagraphElement | MentionElement | ImageElement;
type CustomText = { text: string };

type ParagraphElement = {
    type: 'paragraph';
    children: CustomText[];
};

type MentionElement = {
    type: 'mention';
    userId: number;
    userName: string
    children: CustomText[];
};

type ImageElement = {
    type: 'image';
    url: string;
    children: CustomText[];
};

declare module 'slate' {
    interface CustomTypes {
        Editor: ReactEditor;
        Element: CustomElement;
        Text: CustomText;
    }
}
interface IProps {
    packageID: string | number,
    users: PlmUser[]
    onClose: () => void
    onUpdate: ()=>void
}
const RichTextEditor: React.FC<IProps> = (props) => {
    const { message } = App.useApp();
    const editorBoxRef = useRef<HTMLDivElement>(null);
    const editor = useMemo(() => withMentions(withReact(createEditor())), []);
    const ref = useRef<HTMLDivElement | null>(null)
    const [target, setTarget] = useState<Range | null>(null);
    const [index, setIndex] = useState(0);
    const [value, setValue] = useState(initialValue);
    const [isEmpty, setIsEmpty] = useState(true);
    const checkEditorEmpty = useCallback(() => {
        if (!editor || editor.children.length === 0) return true
        return Editor.string(editor, []) === '' && !(editor.children.length > 0 && !!editor.children.find((v: any)=>v.type === 'image'));
    }, [editor.children]);

    const { ref: inputImgRef, onImageSelectChange } = usePlmImageUpload({
        workpackageID: props.packageID,
        onUploadSuccess: (path: string) => {
            insertImage(editor, path)
        }
    })

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (target) {
                switch (event.key) {
                    case 'ArrowDown':
                        event.preventDefault();
                        setIndex(index >= props.users.length - 1 ? 0 : index + 1);
                        break;
                    case 'ArrowUp':
                        event.preventDefault();
                        setIndex(index <= 0 ? props.users.length - 1 : index - 1);
                        break;
                    case 'Tab':
                    case 'Enter':
                        event.preventDefault();
                        Transforms.select(editor, target);
                        insertMention(editor, props.users[index]);
                        setTarget(null);
                        break;
                    case 'Escape':
                        event.preventDefault();
                        setTarget(null);
                        break;
                }
            }
        },
        [index, target]
    );

    const handleChange = useCallback(() => {
        const { selection } = editor;

        if (selection && Range.isCollapsed(selection)) {
            const [start] = Range.edges(selection)
            const wordBefore = Editor.before(editor, start, { distance: 1 });
            const beforeRange = wordBefore && Editor.range(editor, wordBefore, start);
            const beforeText = beforeRange && Editor.string(editor, beforeRange);
            const beforeMatch = beforeText && beforeText.match(/^@$/)
            if (beforeMatch && beforeRange) {
                setTarget(beforeRange)
                setIndex(0)
                return
            }
        }
        setTarget(null)
        setIsEmpty(checkEditorEmpty())
    }, [editor]);

    const insertMention = (editor: Editor, user: PlmUser) => {
        const mention: MentionElement = {
            type: 'mention',
            userId: user.id,
            userName: user.name,
            children: [{ text: user.name }],
        };
        Transforms.insertNodes(editor, mention);
        Transforms.move(editor);
    };
    // 渲染元素
    const renderElement = useCallback((props: RenderElementProps) => {
        const { attributes, children, element } = props
        switch (element.type) {
            case 'mention':
                return <MentionComponent {...props} element={element} />;
            case 'image':
                return <ImageComponent {...props} element={element} />
            default:
                return <p {...attributes}>{children}</p>
        }
    }, []);
    useEffect(() => {
        
        if (editor && target && props.users.length > 0 && ref.current) {

            const el = ref.current
            if (!el) return
            const domRange = ReactEditor.toDOMRange(editor, target)
            const rect = domRange.getBoundingClientRect()
            const boxrect = editorBoxRef.current?.getBoundingClientRect();
            if (!rect || !boxrect) return
            const top = `${(rect.top - boxrect.top) + window.scrollY + 30}px`;
            const left = `${(rect.left - boxrect.left) + window.scrollX}px`;

            el.style.top = top
            el.style.left = left
        }
        
    }, [props.users.length, editor, index, target])

    const { loading: sendLoading, run: sendMsg } = useRequest(postWorkPackageMsgAPI, {
        manual: true,
        onSuccess: (res) => {
            message.success("提交成功~")
            Transforms.delete(editor, {
                at: {
                    anchor: Editor.start(editor, []),
                    focus: Editor.end(editor, []),
                },
            })
            props.onUpdate()
        },
        onError: (err) => {
            console.error("提交失败", err)
        }
    })
    
    const onSend = () => {
        if(sendLoading || isEmpty) return
        const htmlContent = serializeToHtml(editor.children);

        sendMsg(props.packageID, {
            comment: {
                raw: htmlContent,
            },
        })
    }

    return (
        <div ref={editorBoxRef} style={{ position: 'relative' }}>
            <div className='bg-gray-100 border border-solid border-gray-200'>
                <div className='flex gap-2 border-0 border-b border-solid border-gray-200 p-1'>
                    <div onClick={() => { inputImgRef.current && inputImgRef.current.click()}} className='flex p-1 text-[20px] cursor-pointer text-gray-600 hover:bg-gray-200 hover:text-gray-800'>
                        <ImageSvg />
                        <input ref={inputImgRef} onChange={onImageSelectChange} type='file' className='absolute top-0 left-0 h-0 w-0 hidden' />
                    </div>
                    
                </div>
                <Slate initialValue={initialValue} editor={editor} onChange={handleChange}>
                    <Editable
                        className='min-h-[100px] w-full max-h-[400px] overflow-auto bg-white px-2'
                        placeholder='评论'
                        renderElement={renderElement}
                        onKeyDown={handleKeyDown}
                    />
                    {target && props.users.length > 0 && (
                        <div
                            ref={ref}
                            style={{
                                top: '-9999px',
                                left: '-9999px',
                                position: 'absolute',
                                zIndex: 1,
                                padding: '3px',
                                background: 'white',
                                borderRadius: '4px',
                                boxShadow: '0 1px 5px rgba(0,0,0,.2)',
                                minWidth: 220,
                                maxHeight: '180px',
                                overflowY: 'auto',
                            }}
                            data-cy="mentions-portal"
                        >
                            {props.users.map((v, i) => (
                                <div
                                    key={i}
                                    onClick={() => {
                                        Transforms.select(editor, target)
                                        insertMention(editor, v)
                                        setTarget(null)
                                    }}
                                    style={{
                                        padding: '1px 3px',
                                        borderRadius: '3px',
                                        cursor: 'pointer',
                                        background: i === index ? '#B4D5FF' : 'transparent',
                                    }}
                                >
                                    {v.name}
                                </div>
                            ))}
                        </div>
                    )}
                </Slate>
            </div>
            <div className="mt-4 flex justify-end gap-4">
                <Button onClick={props.onClose}>取消</Button>
                <Button type="primary" onClick={onSend} disabled={isEmpty}>提交</Button>
            </div>
        </div>
    );
};

export default RichTextEditor;
type CustomEditor = BaseEditor & ReactEditor & {
    nodeToDecorations?: Map<Element, Range[]>
}
const insertImage = (editor: CustomEditor, url: string) => {
    const text = { text: '' }
    const image: ImageElement = { type: 'image', url, children: [text] }
    Transforms.insertNodes(editor, image)
    const paragraph: ParagraphElement = {
        type: 'paragraph',
        children: [{ text: '' }],
    }
    Transforms.insertNodes(editor, paragraph)
}
const withMentions = (editor: CustomEditor) => {
    const { isInline, isVoid, markableVoid, insertData } = editor

    editor.isInline = (element: SlateElement) => {
        if(element.type === 'mention') return true
        return isInline(element)
    }

    editor.isVoid = (element: SlateElement) => {
        if (element.type === 'mention') return true
        if (element.type === 'image') return true
        return isVoid(element)
    }

    editor.markableVoid = (element: SlateElement) => {
        return element.type === 'mention' || markableVoid(element)
    }

    editor.insertData = data => {
        const text = data.getData('text/plain')
        const { files } = data

        if (files && files.length > 0) {
            Array.from(files).forEach(file => {
                const reader = new FileReader()
                const [mime] = file.type.split('/')

                if (mime === 'image') {
                    reader.addEventListener('load', () => {
                        const url = reader.result
                        insertImage(editor, url as string)
                    })

                    reader.readAsDataURL(file)
                }
            })
        } else {
            insertData(data)
        }
    }

    return editor
}

type RenderElementPropsFor<T> = RenderElementProps & {
    element: T
}
// 自定义组件
const MentionComponent: React.FC<RenderElementPropsFor<MentionElement>> = ({ attributes, children, element }) => {
    return (
        <a
            {...attributes}
            className='mention'
            href={`/users/${element.userId}`}
            data-mention={`@${element.userName}`}
            title={`@${element.userName}`}
            contentEditable={false}
            style={{
                padding: '2px 4px',
                margin: '0 2px',
                backgroundColor: '#eee',
                borderRadius: '4px',
            }}
        >
            @{element.userName}
        </a>
    );
};
const ImageComponent = ({
    attributes,
    children,
    element,
}: RenderElementPropsFor<ImageElement>) => {
    // const editor = useSlateStatic()
    // const path = ReactEditor.findPath(editor, element)
    return (
        <div {...attributes}>
            {children}
            <p
                contentEditable={false}
                className="relative group"
                style={{
                    display: "inline-flex",
                    maxWidth: "100%",
                    alignItems: "flex-start",
                    position: "relative",
                    zIndex: 1,
                }}
            >
                <img
                    src={element.url}
                    style={{
                        flexGrow: 1,
                        flexShrink: 1,
                        maxWidth: "100%",
                        height: "auto"
                    }}
                />
                {/* <div onClick={() => Transforms.removeNodes(editor, { at: path })} className="hidden group-hover:flex absolute left-0 top-0 bg-white p-2 text-xs cursor-pointer">
                    删除
                </div> */}
            </p>
        </div>
    )
}
interface PlmUser{
    id: number;
    name: string;
}